import React, { useState, useEffect } from "react";
import { Modal, Avatar } from "antd";
import { useDispatch } from "react-redux";
import { bulkUpdateAttendance } from "../../../core/data/redux/employeesAttendanceSlice";
import { formatDateTime } from "../../../utils/dateUtils";
import { toast } from "react-toastify";

interface BulkAttendanceRegularizeModalProps {
  open: boolean;
  items: any[];
  onClose: () => void;
  onSuccess?: () => void;
}

interface ItemCustomConfig {
  actionType?: "accept" | "reject";
  attendanceStatus?: "PRESENT" | "HALF_DAY";
  halfDayType?: string;
  remark?: string;
  checkinLocationMismatch?: boolean;
  checkoutLocationMismatch?: boolean;
  earlyLeaving?: boolean;
  lateComing?: boolean;
}

const BulkAttendanceRegularizeModal: React.FC<BulkAttendanceRegularizeModalProps> = ({
  open,
  items,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  // Selected items list (allows removing items inside modal)
  const [selectedList, setSelectedList] = useState<any[]>([]);
  const [checkedIds, setCheckedIds] = useState<any[]>([]);
  const [activeItemId, setActiveItemId] = useState<any>(null);

  // Master form state (applied to all selected items by default)
  const [actionType, setActionType] = useState<string>("accept");
  const [attendanceStatus, setAttendanceStatus] = useState<string>("PRESENT");
  const [halfDayType, setHalfDayType] = useState<string>("");
  const [dayType, setDayType] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("Approved by CO");
  const [remarkError, setRemarkError] = useState<string>("");

  // Options checkboxes for Accept - default all checked
  const [checkinLocationMismatch, setCheckinLocationMismatch] = useState<boolean>(true);
  const [checkoutLocationMismatch, setCheckoutLocationMismatch] = useState<boolean>(true);
  const [earlyLeaving, setEarlyLeaving] = useState<boolean>(true);
  const [lateComing, setLateComing] = useState<boolean>(true);

  // Per-item override configs (optional granular customization)
  const [customConfigs, setCustomConfigs] = useState<Record<number, ItemCustomConfig>>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  // Sync selectedList with items prop when modal opens
  useEffect(() => {
    if (open) {
      const list = items || [];
      setSelectedList(list);
      setCheckedIds(list.map((item) => item.id ?? item.key));
      const initialId = list.length > 0 ? (list[0].id ?? list[0].key) : null;
      setActiveItemId(initialId ?? null);
      setActionType("accept");
      setAttendanceStatus("PRESENT");
      setHalfDayType("");
      setDayType("");
      setRemarks("Approved by CO");
      setRemarkError("");
      setCheckinLocationMismatch(true);
      setCheckoutLocationMismatch(true);
      setEarlyLeaving(true);
      setLateComing(true);
      setCustomConfigs({});
      setEditingItemId(null);
      setShowConfirmModal(false);
    }
  }, [open, items]);

  const randomAvatarColor = (name: string) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#d46b08",
      "#1890ff",
      "#eb2f96",
    ];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getEmployeeImage = (rec: any): string | null => {
    const emp = rec?.employee;
    const det = emp?.details;
    if (det?.image) {
      return det.image.startsWith("data:") || det.image.startsWith("http")
        ? det.image
        : `data:image/jpeg;base64,${det.image}`;
    }
    if (emp?.sap_employee_images?.image) {
      const sapImg = emp.sap_employee_images.image;
      return sapImg.startsWith("data:") || sapImg.startsWith("http")
        ? sapImg
        : `data:image/jpeg;base64,${sapImg}`;
    }
    if (emp?.image) {
      return emp.image.startsWith("data:") || emp.image.startsWith("http")
        ? emp.image
        : `data:image/jpeg;base64,${emp.image}`;
    }
    return null;
  };

  const handleRemoveItem = (idToRemove: number) => {
    const updated = selectedList.filter((item) => (item.id ?? item.key) !== idToRemove);
    setSelectedList(updated);
    setCheckedIds((prev) => prev.filter((x) => x !== idToRemove));
    if (activeItemId === idToRemove) {
      setActiveItemId(updated.length > 0 ? (updated[0].id ?? updated[0].key) : null);
    }
    if (updated.length === 0) {
      onClose();
    }
  };

  const toggleCheckItem = (id: any) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const allIds = selectedList.map((item) => item.id ?? item.key);
    if (checkedIds.length === allIds.length) {
      setCheckedIds([]);
    } else {
      setCheckedIds(allIds);
    }
  };

  const activeSelectedList = selectedList.filter((item) =>
    checkedIds.includes(item.id ?? item.key)
  );

  const activeRecord =
    selectedList.find((item) => (item.id ?? item.key) === activeItemId) ||
    selectedList[0] ||
    null;

  const updateItemConfig = (itemId: number, patch: Partial<ItemCustomConfig>) => {
    setCustomConfigs((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        ...patch,
      },
    }));
  };

  const handleQuickRemark = (text: string) => {
    setRemarks(text);
    setRemarkError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!actionType) {
      toast.warning("Please select an Action Type.");
      return;
    }

    if (actionType === "reject" && !remarks.trim()) {
      setRemarkError("Remark is required for rejection.");
      return;
    }

    if (actionType === "accept" && attendanceStatus === "HALF_DAY" && !halfDayType) {
      toast.warning("Please select Half Day Type.");
      return;
    }

    if (activeSelectedList.length === 0) {
      toast.warning("No records selected to regularize.");
      return;
    }

    // Open confirmation popup modal before performing action
    setShowConfirmModal(true);
  };

  const executeActualSubmit = async () => {
    // Build requests array matching the cURL specification
    const requests = activeSelectedList.map((item) => {
      const id = item.id ?? item.key;
      const config = customConfigs[id] || {};

      const effectiveAction = config.actionType || actionType;
      const effectiveStatus = config.attendanceStatus || attendanceStatus;
      const effectiveHalfDay = config.halfDayType || halfDayType;
      const effectiveRemark = config.remark !== undefined ? config.remark : remarks;

      if (effectiveAction === "accept") {
        if (effectiveStatus === "HALF_DAY") {
          return {
            id: Number(id),
            is_verified: "VERIFIED",
            attendance_status: "HALF_DAY",
            half_day_type: effectiveHalfDay || "FIRST_HALF",
            regularize_remark: effectiveRemark || "Approved for half day",
          };
        } else {
          const itemCheckinLocMismatch =
            config.checkinLocationMismatch !== undefined
              ? config.checkinLocationMismatch
              : checkinLocationMismatch;
          const itemCheckoutLocMismatch =
            config.checkoutLocationMismatch !== undefined
              ? config.checkoutLocationMismatch
              : checkoutLocationMismatch;
          const itemEarlyLeaving =
            config.earlyLeaving !== undefined ? config.earlyLeaving : earlyLeaving;
          const itemLateComing =
            config.lateComing !== undefined ? config.lateComing : lateComing;

          const req: any = {
            id: Number(id),
            is_verified: "VERIFIED",
            attendance_status: "PRESENT",
            regularize_remark: effectiveRemark || "Approved by CO",
            checkin_location_mismatch: Boolean(itemCheckinLocMismatch),
            checkout_location_mismatch: Boolean(itemCheckoutLocMismatch),
            checkin_face_mismatch: false,
            checkout_face_mismatch: false,
          };

          if (itemEarlyLeaving) req.early_leaving = "true";
          if (itemLateComing) req.late_coming = "true";

          return req;
        }
      } else {
        // REJECTED
        const req: any = {
          id: Number(id),
          is_verified: "REJECTED",
          regularize_remark: effectiveRemark || "Not approved",
        };
        if (dayType === "HALF_DAY" && halfDayType) {
          req.half_day_type = halfDayType;
        }
        return req;
      }
    });

    console.log("Submitting bulk attendance regularize payload:", { requests });

    try {
      setIsSubmitting(true);
      const res = await dispatch(bulkUpdateAttendance({ requests })).unwrap();
      setIsSubmitting(false);

      const summary = res?.data?.summary || res?.summary;
      const results: any[] = res?.data?.results || res?.results || [];

      if (summary && summary.failed > 0) {
        const failedItems = results.filter((r) => r.status === "FAILED");
        const failedMsg = failedItems
          .map((r) => `ID ${r.id}: ${r.message || "Failed"}`)
          .slice(0, 3)
          .join(" | ");

        if (summary.successful === 0) {
          toast.error(
            `Bulk attendance regularization failed: ${failedMsg}`
          );
        } else {
          toast.warning(
            `${summary.successful} succeeded, ${summary.failed} failed: ${failedMsg}`
          );
        }
      } else {
        toast.success(
          res?.message || `${requests.length} attendance records regularized successfully.`
        );
      }

      setShowConfirmModal(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setIsSubmitting(false);
      const msg = typeof err === "string" ? err : err?.message || "Failed to update bulk attendance.";
      toast.error(msg);
    }
  };

  const isFormValid = () => {
    if (!actionType) return false;
    if (actionType === "reject" && !remarks.trim()) return false;
    if (actionType === "accept" && attendanceStatus === "HALF_DAY" && !halfDayType) return false;
    return true;
  };

  return (
    <>
      <Modal
        title={
          <div className="d-flex align-items-center justify-content-between pe-4">
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "#FEE2E2",
                  color: "#C0282C",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                }}
              >
                <i className="ti ti-checklist" />
              </div>
              <div>
                <span style={{ fontSize: "17px", fontWeight: "600", color: "#111827" }}>
                  Approve Action
                </span>
                <span
                  className="badge ms-2"
                  style={{
                    backgroundColor: "#E0E7FF",
                    color: "#3730A3",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderRadius: "12px",
                    padding: "4px 8px",
                  }}
                >
                  Bulk Action ({activeSelectedList.length} Selected)
                </span>
              </div>
            </div>
          </div>
        }
        open={open}
        onCancel={onClose}
        footer={false}
        width={820}
        destroyOnClose
        style={{ top: 20 }}
        styles={{
          body: {
            maxHeight: "calc(90vh - 75px)",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "10px 18px",
          },
        }}
      >
        <div className="row g-3 pt-1">
          {/* Left Column: Selected Employees List */}
          <div className="col-lg-5 col-md-5">
            <div
              style={{
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "10px 12px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    id="select_all_attendance_records"
                    checked={
                      selectedList.length > 0 &&
                      checkedIds.length === selectedList.length
                    }
                    onChange={toggleSelectAll}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#EA580C",
                    }}
                  />
                  <label
                    htmlFor="select_all_attendance_records"
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#374151",
                      cursor: "pointer",
                      margin: 0,
                    }}
                  >
                    Select All ({checkedIds.length}/{selectedList.length})
                  </label>
                </div>
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  Click to inspect
                </span>
              </div>

              <div
                style={{
                  maxHeight: "440px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {selectedList.map((item, idx) => {
                  const id = item.id ?? item.key;
                  const employee = item.employee;
                  const details = employee?.details;
                  const first = details?.firstname || "";
                  const last = details?.lastname || details?.lsastname || "";
                  const fullName = `${first} ${last}`.trim() || `Employee #${item.emp_id || id}`;
                  const empId = employee?.empId || item.emp_id || "-";
                  const deptWing = details?.sap_department_designation_wings;
                  const designation =
                    deptWing?.designation_name || details?.designation || "";
                  const department =
                    deptWing?.department_name || details?.department || "";
                  const deptInfo = [designation, department]
                    .filter(Boolean)
                    .join(" / ");
                  const isSelected = activeItemId === id;
                  const isChecked = checkedIds.includes(id);
                  const hasCustom = Boolean(customConfigs[id]);
                  const isEditingThis = editingItemId === id;

                  const displayDate = item.check_in_time
                    ? formatDateTime(item.check_in_time)
                    : item.date
                      ? formatDateTime(item.date)
                      : item.created_at
                        ? formatDateTime(item.created_at)
                        : "N/A";

                  return (
                    <div
                      key={id || idx}
                      onClick={() => {
                        setActiveItemId(id);
                        if (!isChecked) {
                          toggleCheckItem(id);
                        }
                      }}
                      style={{
                        backgroundColor: isSelected
                          ? "#EFF6FF"
                          : isChecked
                            ? "#FFFFFF"
                            : "#F9FAFB",
                        border: isSelected
                          ? "1px solid #2563EB"
                          : isChecked
                            ? "1px solid #BFDBFE"
                            : "1px dashed #D1D5DB",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        marginBottom: "6px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        boxShadow: isSelected
                          ? "0 1px 4px rgba(37,99,235,0.12)"
                          : "none",
                        opacity: isChecked ? 1 : 0.6,
                      }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ minWidth: 0, flex: 1 }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={isChecked}
                            onChange={() => { }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCheckItem(id);
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              accentColor: "#EA580C",
                              flexShrink: 0,
                            }}
                          />
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              minWidth: "36px",
                              minHeight: "36px",
                              maxWidth: "36px",
                              maxHeight: "36px",
                              borderRadius: "50%",
                              overflow: "hidden",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#e5e7eb",
                              border: isChecked ? (isSelected ? "1.5px solid #2563EB" : "1px solid #93C5FD") : "1px solid #E5E7EB",
                            }}
                          >
                            {getEmployeeImage(item) ? (
                              <img
                                src={getEmployeeImage(item)!}
                                alt={fullName}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  backgroundColor: randomAvatarColor(fullName),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#ffffff",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                }}
                              >
                                {first ? first.charAt(0).toUpperCase() : "E"}
                              </div>
                            )}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: isChecked ? (isSelected ? "#1E40AF" : "#1D4ED8") : "#6b7280",
                                lineHeight: 1.2,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={fullName}
                            >
                              {fullName}
                            </div>
                            <div
                              style={{
                                fontSize: "10px",
                                color: "#6b7280",
                                marginTop: "2px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <span
                                className="badge bg-light text-dark"
                                style={{ fontSize: "9px", padding: "2px 5px", flexShrink: 0 }}
                              >
                                ID: {empId}
                              </span>
                              {deptInfo && (
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={deptInfo}
                                >
                                  {deptInfo}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div
                          className="d-flex align-items-center gap-1 ms-2"
                          style={{ flexShrink: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            title="Override individual settings"
                            onClick={() =>
                              setEditingItemId(isEditingThis ? null : id)
                            }
                            style={{
                              fontSize: "13px",
                              color: hasCustom ? "#2563EB" : "#6B7280",
                            }}
                          >
                            <i className="ti ti-adjustments" />
                          </button>
                        </div>
                      </div>

                      {/* Date Details & Status */}
                      <div
                        className="d-flex align-items-center justify-content-between mt-2 pt-1"
                        style={{
                          fontSize: "11px",
                          color: "#4b5563",
                        }}
                      >
                        <span>
                          <b style={{ color: isChecked ? (isSelected ? "#1E40AF" : "#2563EB") : "#4b5563" }}>Date:</b>{" "}
                          <span style={{ color: isChecked ? "#374151" : "#6b7280" }}>{displayDate}</span>
                        </span>
                        {hasCustom && (
                          <span
                            className={`badge ${customConfigs[id]?.actionType === "reject"
                              ? "bg-danger"
                              : "bg-success"
                              }`}
                            style={{ fontSize: "9px" }}
                          >
                            {customConfigs[id]?.actionType === "reject"
                              ? "Reject"
                              : "Accept (Approve)"}
                          </span>
                        )}
                      </div>

                      {/* Check-in / Check-out Remarks */}
                      {(() => {
                        const checkinRemark =
                          item.checkin_remark ||
                          item.check_in_remark ||
                          item.checkinRemark;
                        const checkoutRemark =
                          item.checkout_remark ||
                          item.check_out_remark ||
                          item.checkoutRemark;
                        if (!checkinRemark && !checkoutRemark) return null;
                        return (
                          <div
                            className="mt-1 pt-1"
                            style={{
                              fontSize: "10.5px",
                              color: "#4b5563",
                              borderTop: "1px dashed #e5e7eb",
                              lineHeight: 1.3,
                            }}
                          >
                            {checkinRemark && (
                              <div className="text-truncate" title={checkinRemark}>
                                <span style={{ fontWeight: 600, color: "#166534" }}>Check-In Remark: </span>
                                <span>{checkinRemark}</span>
                              </div>
                            )}
                            {checkoutRemark && (
                              <div className="text-truncate" title={checkoutRemark}>
                                <span style={{ fontWeight: 600, color: "#9a3412" }}>Check-Out Remark: </span>
                                <span>{checkoutRemark}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Per-item customization collapsible */}
                      {isEditingThis && (
                        <div
                          className="mt-2 pt-2"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            borderTop: "1px solid #bfdbfe",
                            fontSize: "11px",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <strong style={{ color: "#1e40af" }}>Override for {first || "this item"}:</strong>
                            {hasCustom && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-danger"
                                style={{ fontSize: "10px" }}
                                onClick={() => {
                                  const copy = { ...customConfigs };
                                  delete copy[id];
                                  setCustomConfigs(copy);
                                }}
                              >
                                Reset
                              </button>
                            )}
                          </div>

                          <div className="row g-1">
                            <div className="col-6">
                              <label style={{ fontSize: "10px", color: "#6b7280" }}>Action</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                value={customConfigs[id]?.actionType || actionType || "accept"}
                                onChange={(e) =>
                                  updateItemConfig(id, {
                                    actionType: e.target.value as any,
                                  })
                                }
                              >
                                <option value="accept">Accept (Approve)</option>
                                <option value="reject">Reject</option>
                              </select>
                            </div>
                            <div className="col-6">
                              <label style={{ fontSize: "10px", color: "#6b7280" }}>Status</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                value={
                                  customConfigs[id]?.attendanceStatus ||
                                  attendanceStatus ||
                                  "PRESENT"
                                }
                                onChange={(e) =>
                                  updateItemConfig(id, {
                                    attendanceStatus: e.target.value as any,
                                  })
                                }
                              >
                                <option value="PRESENT">Full Day (Present)</option>
                                <option value="HALF_DAY">Half Day</option>
                              </select>
                            </div>

                            {(customConfigs[id]?.attendanceStatus === "HALF_DAY" ||
                              (!customConfigs[id]?.attendanceStatus &&
                                attendanceStatus === "HALF_DAY")) && (
                                <div className="col-12 mt-1">
                                  <label style={{ fontSize: "10px", color: "#6b7280" }}>Half Day Type</label>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ fontSize: "11px", padding: "2px 6px" }}
                                    value={
                                      customConfigs[id]?.halfDayType ||
                                      halfDayType ||
                                      "FIRST_HALF"
                                    }
                                    onChange={(e) =>
                                      updateItemConfig(id, {
                                        halfDayType: e.target.value,
                                      })
                                    }
                                  >
                                    <option value="FIRST_HALF">First Half</option>
                                    <option value="SECOND_HALF">Second Half</option>
                                  </select>
                                </div>
                              )}

                            <div className="col-12 mt-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ fontSize: "11px", padding: "3px 6px" }}
                                placeholder="Specific remark for this employee"
                                value={customConfigs[id]?.remark ?? ""}
                                onChange={(e) =>
                                  updateItemConfig(id, { remark: e.target.value })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-auto pt-2"
                style={{
                  borderTop: "1px solid #e5e7eb",
                  fontSize: "11px",
                  color: "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{activeSelectedList.length} records ready</span>
                <span>Bulk settings apply to all</span>
              </div>
            </div>
          </div>

          {/* Right Column: Approval Action Form */}
          <div className="col-lg-7 col-md-7">
            {activeRecord && (
              <div
                className="p-2 mb-2"
                style={{
                  backgroundColor: "#F3F4F6",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                }}
              >
                {/* Top Row: Employee Profile + Date */}
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        minWidth: "40px",
                        minHeight: "40px",
                        maxWidth: "40px",
                        maxHeight: "40px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#e5e7eb",
                        border: "1px solid #d1d5db",
                      }}
                    >
                      {getEmployeeImage(activeRecord) ? (
                        <img
                          src={getEmployeeImage(activeRecord)!}
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "50%",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            backgroundColor: randomAvatarColor(
                              activeRecord.employee?.details?.firstname || "E"
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            fontWeight: 600,
                            fontSize: "14px",
                          }}
                        >
                          {(activeRecord.employee?.details?.firstname || "E")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {(() => {
                          const d = activeRecord.employee?.details;
                          const fn = `${d?.firstname || ""} ${d?.lastname || d?.lsastname || ""
                            }`.trim();
                          return (
                            fn ||
                            `Employee #${activeRecord.employee?.empId ||
                            activeRecord.emp_id ||
                            (activeRecord.id ?? activeRecord.key)
                            }`
                          );
                        })()}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6B7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Emp ID:{" "}
                        <strong>
                          {activeRecord.employee?.empId ||
                            activeRecord.emp_id ||
                            "-"}
                        </strong>
                        {(() => {
                          const dw =
                            activeRecord.employee?.details
                              ?.sap_department_designation_wings;
                          const desig =
                            dw?.designation_name ||
                            activeRecord.employee?.details?.designation ||
                            "";
                          const dpt =
                            dw?.department_name ||
                            activeRecord.employee?.details?.department ||
                            "";
                          const sub = [desig, dpt].filter(Boolean).join(" / ");
                          return sub ? ` • ${sub}` : "";
                        })()}
                      </div>
                    </div>
                  </div>

                  {(activeRecord.check_in_time ||
                    activeRecord.date ||
                    activeRecord.created_at) && (
                      <span
                        className="badge bg-white text-dark border ms-2"
                        style={{ fontSize: "11px", fontWeight: 500, padding: "4px 8px", flexShrink: 0 }}
                      >
                        Date:{" "}
                        {formatDateTime(
                          activeRecord.check_in_time ||
                          activeRecord.date ||
                          activeRecord.created_at
                        )}
                      </span>
                    )}
                </div>

                {/* Active Record Remarks Row */}
                {(() => {
                  const checkinRemark =
                    activeRecord.checkin_remark ||
                    activeRecord.check_in_remark ||
                    activeRecord.checkinRemark;
                  const checkoutRemark =
                    activeRecord.checkout_remark ||
                    activeRecord.check_out_remark ||
                    activeRecord.checkoutRemark;
                  if (!checkinRemark && !checkoutRemark) return null;
                  return (
                    <div
                      className="mt-2 pt-2 d-flex flex-column gap-1"
                      style={{
                        borderTop: "1px dashed #d1d5db",
                        fontSize: "11.5px",
                      }}
                    >
                      {checkinRemark && (
                        <div className="d-flex align-items-baseline gap-2">
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#DCFCE7",
                              color: "#166534",
                              border: "1px solid #BBF7D0",
                              fontSize: "10px",
                              fontWeight: 600,
                              padding: "2px 6px",
                              flexShrink: 0,
                            }}
                          >
                            Check-in Remark
                          </span>
                          <span style={{ color: "#374151", wordBreak: "break-word" }}>
                            {checkinRemark}
                          </span>
                        </div>
                      )}
                      {checkoutRemark && (
                        <div className="d-flex align-items-baseline gap-2">
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#FFEDD5",
                              color: "#9A3412",
                              border: "1px solid #FED7AA",
                              fontSize: "10px",
                              fontWeight: 600,
                              padding: "2px 6px",
                              flexShrink: 0,
                            }}
                          >
                            Check-out Remark
                          </span>
                          <span style={{ color: "#374151", wordBreak: "break-word" }}>
                            {checkoutRemark}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="row g-2">
                {/* Action Type */}
                <div className="col-md-12">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                    Action Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={actionType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setActionType(val);
                      setRemarkError("");
                      if (val === "reject") {
                        setAttendanceStatus("ABSENT");
                        setRemarks("Not approved");
                      } else if (val === "accept") {
                        setAttendanceStatus("PRESENT");
                        setRemarks("Approved by CO");
                        setCheckinLocationMismatch(true);
                        setCheckoutLocationMismatch(true);
                        setEarlyLeaving(true);
                        setLateComing(true);
                      }
                    }}
                    style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                  >
                    <option value="">Select Option</option>
                    <option value="accept">Accept (Approve)</option>
                    <option value="reject">Reject</option>
                  </select>
                </div>

                {/* Attendance Status (when Accept) */}
                {actionType === "accept" && (
                  <div className="col-md-12">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                      Attendance Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={attendanceStatus}
                      onChange={(e) => {
                        setAttendanceStatus(e.target.value);
                        if (e.target.value === "HALF_DAY") {
                          setHalfDayType("FIRST_HALF");
                          setRemarks("Approved for 1st half only");
                        } else {
                          setHalfDayType("");
                          setRemarks("Approved by CO");
                        }
                      }}
                      style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                    >
                      <option value="PRESENT">Full Day (Present)</option>
                      <option value="HALF_DAY">Half Day</option>
                    </select>
                  </div>
                )}

                {/* Half Day Type (when Accept + Half Day) */}
                {actionType === "accept" && attendanceStatus === "HALF_DAY" && (
                  <div className="col-md-12">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "13px", color: "#374151" }}>
                      Half Day Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={halfDayType}
                      onChange={(e) => {
                        setHalfDayType(e.target.value);
                        if (e.target.value === "FIRST_HALF") {
                          setRemarks("Approved for 1st half only");
                        } else if (e.target.value === "SECOND_HALF") {
                          setRemarks("Approved for 2nd half only");
                        }
                      }}
                      style={{ borderRadius: "6px" }}
                    >
                      <option value="">Select Option</option>
                      <option value="FIRST_HALF">First Half</option>
                      <option value="SECOND_HALF">Second Half</option>
                    </select>
                  </div>
                )}

                {/* Options Checkboxes (when Accept + Full Day) */}
                {actionType === "accept" && attendanceStatus === "PRESENT" && (
                  <div className="col-md-12">
                    <label
                      className="form-label"
                      style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}
                    >
                      Options
                    </label>
                    <div
                      style={{
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "8px 10px",
                      }}
                    >
                      <div className="row g-2">
                        <div className="col-sm-6">
                          <div className="form-check d-flex align-items-center gap-2 m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="bulk_checkinLocationMismatch"
                              checked={checkinLocationMismatch}
                              onChange={(e) => setCheckinLocationMismatch(e.target.checked)}
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="bulk_checkinLocationMismatch"
                              style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                            >
                              Check-in Location Mismatch
                            </label>
                          </div>
                        </div>

                        <div className="col-sm-6">
                          <div className="form-check d-flex align-items-center gap-2 m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="bulk_checkoutLocationMismatch"
                              checked={checkoutLocationMismatch}
                              onChange={(e) => setCheckoutLocationMismatch(e.target.checked)}
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="bulk_checkoutLocationMismatch"
                              style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                            >
                              Check-out Location Mismatch
                            </label>
                          </div>
                        </div>

                        <div className="col-sm-6">
                          <div className="form-check d-flex align-items-center gap-2 m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="bulk_earlyLeaving"
                              checked={earlyLeaving}
                              onChange={(e) => setEarlyLeaving(e.target.checked)}
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="bulk_earlyLeaving"
                              style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                            >
                              Early Leaving
                            </label>
                          </div>
                        </div>

                        <div className="col-sm-6">
                          <div className="form-check d-flex align-items-center gap-2 m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="bulk_lateComing"
                              checked={lateComing}
                              onChange={(e) => setLateComing(e.target.checked)}
                              style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="bulk_lateComing"
                              style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                            >
                              Late Coming
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Day Type (when Reject) */}
                {actionType === "reject" && (
                  <div className="col-md-12">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                      Day Type
                    </label>
                    <select
                      className="form-select"
                      value={dayType}
                      onChange={(e) => {
                        setDayType(e.target.value);
                        if (e.target.value === "FULL_DAY") {
                          setHalfDayType("");
                        }
                      }}
                      style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                    >
                      <option value="">Select Option</option>
                      <option value="FULL_DAY">Full Day</option>
                      <option value="HALF_DAY">Half Day</option>
                    </select>
                  </div>
                )}

                {/* Half Day Type (when Reject + Half Day) */}
                {actionType === "reject" && dayType === "HALF_DAY" && (
                  <div className="col-md-12">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                      Half Day Type
                    </label>
                    <select
                      className="form-select"
                      value={halfDayType}
                      onChange={(e) => setHalfDayType(e.target.value)}
                      style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                    >
                      <option value="">Select Option</option>
                      <option value="FIRST_HALF">First Half</option>
                      <option value="SECOND_HALF">Second Half</option>
                    </select>
                  </div>
                )}

                {/* Remarks */}
                <div className="col-md-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label m-0" style={{ fontWeight: 600, fontSize: "12px", color: "#374151" }}>
                      Remarks {actionType === "reject" && <span className="text-danger">*</span>}
                    </label>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>
                      Applies to all selected
                    </span>
                  </div>
                  <textarea
                    className="form-control"
                    placeholder="Remarks"
                    rows={2}
                    value={remarks}
                    onChange={(e) => {
                      setRemarks(e.target.value);
                      if (e.target.value.trim()) setRemarkError("");
                    }}
                    style={{ borderRadius: "6px", fontSize: "13px" }}
                  />
                  {remarkError && (
                    <small className="text-danger d-block mt-1">{remarkError}</small>
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="col-md-12 pt-2">
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-light px-3"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn px-4 d-flex align-items-center gap-2"
                      disabled={!isFormValid() || isSubmitting || activeSelectedList.length === 0}
                      style={{
                        backgroundColor: "#C0282C",
                        borderColor: "#C0282C",
                        color: "#fff",
                        fontSize: "15px",
                        fontWeight: "500",
                        borderRadius: "6px",
                        opacity: !isFormValid() || isSubmitting || activeSelectedList.length === 0 ? 0.6 : 1,
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" />
                          Regularizing...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check" />
                          Regularize ({activeSelectedList.length})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>

      {/* Confirmation Popup Modal */}
      <Modal
        open={showConfirmModal}
        onCancel={() => !isSubmitting && setShowConfirmModal(false)}
        footer={null}
        centered
        width={440}
        styles={{ body: { padding: "24px 20px" } }}
      >
        <div className="text-center">
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: actionType === "reject" ? "#FEE2E2" : "#FEF3C7",
              color: actionType === "reject" ? "#DC2626" : "#D97706",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "28px",
              marginBottom: "16px",
            }}
          >
            <i className={actionType === "reject" ? "ti ti-alert-triangle" : "ti ti-help-circle"} />
          </div>
          <h5 style={{ fontWeight: 600, color: "#111827", marginBottom: "8px" }}>
            Confirm Attendance Regularization
          </h5>
          <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "16px" }}>
            Are you sure you want to <strong>{actionType === "reject" ? "reject" : "approve"}</strong>{" "}
            regularization for{" "}
            <span className="badge bg-primary px-2 py-1" style={{ fontSize: "12px" }}>
              {activeSelectedList.length} record{activeSelectedList.length !== 1 ? "s" : ""}
            </span>
            ?
          </p>

          <div
            className="text-start p-3 mb-3"
            style={{
              backgroundColor: "#F9FAFB",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              fontSize: "12px",
            }}
          >
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Action:</span>
              <span className={`fw-bold ${actionType === "reject" ? "text-danger" : "text-success"}`}>
                {actionType === "reject" ? "Reject" : "Accept (Approve)"}
              </span>
            </div>
            {actionType === "accept" && (
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Status:</span>
                <span className="fw-semibold text-dark">
                  {attendanceStatus === "HALF_DAY"
                    ? `Half Day (${halfDayType === "SECOND_HALF" ? "Second Half" : "First Half"})`
                    : "Full Day (Present)"}
                </span>
              </div>
            )}
            {remarks && (
              <div className="d-flex justify-content-between">
                <span className="text-muted">Remarks:</span>
                <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: "220px" }} title={remarks}>
                  {remarks}
                </span>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <button
              type="button"
              className="btn btn-light px-3"
              onClick={() => setShowConfirmModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn px-4"
              onClick={executeActualSubmit}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#C0282C",
                borderColor: "#C0282C",
                color: "#fff",
                fontWeight: 500,
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Processing...
                </>
              ) : (
                "Yes, Confirm"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BulkAttendanceRegularizeModal;
